import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useContext,
    Fragment,
    useRef,
    useReducer,
    useState,
} from "react";
import { useAuth } from "hooks/Auth";
import { useParams, useLocation } from "react-router-dom";
import {
    apiDel,
    apiGet,
    apiPost,
    apiPut,
    messageFromException,
} from "helpers/Request";
import {
    transform as entryTransformer,
    EntryObjectSchema,
} from "schemas/Entry";
import {
    transform as channelTransformer,
    ChannelObjectSchema,
} from "schemas/Channel";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { AppContext } from "context/App";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import SelectBox from "components/FormElements/SelectBox";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import { SaveIcon, TrashIcon, SupportIcon } from "@heroicons/react/outline";
import { useContextualHelp } from "hooks/Help";
import EntryFields, {
    toFormFields,
    fromFormFields,
} from "Screens/EntryScreen/EntryFields";
import { useRegisterActions, Priority } from "kbar";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { modal } from "components/HeadlessModal";
import { reject } from "lodash";
import { packClasses } from "Utils";

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

export default function EntryEditScreen(props) {
    const { uuid } = useParams();
    const formRef = useRef();
    const location = useLocation();
    const auth = useAuth();
    const history = useHistory();
    const contextualHelp = useContextualHelp();
    const [metaOpen, setMetaOpen] = useState(true);

    const [state, setState] = useReducer(reducer, {
        entry: EntryObjectSchema,
        channel: ChannelObjectSchema,
        channels: [],
        formValues: EntryObjectSchema,
        loading: false,
        saving: false,
    });

    const { setCurrentNav } = useContext(AppContext);

    useRegisterActions(
        [
            {
                id: "entrySave",
                name: "Save",
                shortcut: ["Meta+Enter"],
                keywords: "save",
                perform: () => {
                    setTimeout(() => {
                        formRef.current.submitForm();
                    }, 250);
                },
                priority: Priority.HIGH,
            },
        ],
        [state.entry]
    );

    const onSubmit = (values) => {
        let metaValues = reject(
            fromFormFields(values, state.channel, state.entry),
            (item) =>
                item.value === "" &&
                ["DatetimeMetaProperty", "DateMetaProperty"].indexOf(
                    item.type
                ) !== -1
        );
        const data = Object.assign({}, state.entry, values, {
            entryMeta: metaValues,
            channel_id: state.channel.id,
            meta: undefined,
            created_at: undefined,
        });

        setState({ saving: true });

        const apiAction = state.entry.uuid ? apiPut : apiPost;
        apiAction(
            "/v4/entry/" + (state.entry.uuid ? state.entry.uuid : ""),
            auth.publication,
            data
        )
            .then((resp) => {
                const item = entryTransformer(resp);
                toast.success("Entry saved");
                if (!state.entry.uuid) {
                    // was a new item, so redirect
                    history.push("/app/custom-content/entry/" + item.uuid);
                }
                setState({ entry: item, saving: false, loading: false });
            })
            .catch((err) => {
                setState({ saving: false });
                toast.error("Unable to save your changes, please try again.");
            });
    };

    const onDelete = () => {
        modal.warning({
            title: "Delete Entry",
            children: (
                <div>
                    <div className="mb-4">
                        <strong>
                            Are you sure you want to delete this entry?
                        </strong>
                    </div>
                    <div>
                        <strong>Note:</strong> This action cannot be undone.
                    </div>
                </div>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={deleteEntry}>Delete</DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const deleteEntry = async () => {
        const id = toast.loading("Deleting entry...");

        return apiDel(`/v4/entry/${state.entry.uuid}`, auth.publication)
            .then((resp) => {
                modal.dismiss();
                toast.success("Entry deleted", { id });
                history.push("/app/custom-content/entry");
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to delete entry",
                    children: messageFromException(err),
                });
            });
    };

    const onChannelChange = (value, fieldData) => {
        const newChannel = state.channels.find(
            (channel) => channel.id === value.value
        );
        if (!newChannel) {
            return;
        }
        setState({ channel: newChannel });
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.has("channel")) {
            const channel = state.channels.find(
                (c) => c.uuid === params.get("channel")
            );
            setState({ channel: channel });
        }
    }, [state.channels, location]);

    useEffect(() => {
        setState({
            loading: true,
            entry: EntryObjectSchema,
            channel: ChannelObjectSchema,
        });

        apiGet("/v4/channel", auth.publication, {
            include: ["settings"],
        }).then((resp) => setState({ channels: channelTransformer(resp) }));

        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setState({ loading: false });
            }, 0);
            return;
        }

        apiGet("/v4/entry/" + uuid, auth.publication, { include: ["channel"] })
            .then((response) => {
                const item = entryTransformer(response);

                setState({
                    entry: item,
                    channel: item.channel,
                    loading: false,
                });
                if (item && item?.uuid && item?.channel.settings_panel) {
                    setMetaOpen(false);
                }
            })
            .catch((err) => {
                console.error(err);
                setState({ loading: false });
                modal.error({
                    title: "Unable to load entry",
                    children: messageFromException(err),
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication]);

    useEffect(() => {
        setCurrentNav("custom-content");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({
                formValues: Object.assign(
                    {},
                    state.entry,
                    {
                        channel: {
                            label: state.channel?.title || "Select Channel",
                            value: state.channel?.id || null,
                        },
                    },
                    toFormFields(state.channel, state.entry)
                ),
            });
        });
    }, [state.entry, state.channel]);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: state.formValues,
            });
        });
    }, [state.loading, state.formValues]);

    return (
        <MainBlock className="z-0">
            <Formik
                innerRef={formRef}
                initialValues={state.formValues}
                onSubmit={onSubmit}
                enableReinitialize={false}
            >
                {({ setFieldValue }) => (
                    <Form>
                        <PromptIfDirty />
                        <div className="mx-auto max-w-2xl space-y-4">
                            <Toolbar>
                                <ToolbarGroup>
                                    <ToolbarButton
                                        icon={<SaveIcon />}
                                        type="submit"
                                        disabled={state.saving}
                                        blocking={state.saving}
                                    >
                                        Save
                                    </ToolbarButton>
                                    <ToolbarButton
                                        icon={<TrashIcon />}
                                        onClick={onDelete}
                                        disabled={
                                            state.saving || !state.entry.uuid
                                        }
                                    >
                                        Delete
                                    </ToolbarButton>
                                </ToolbarGroup>
                                <ToolbarGroup className="ml-auto">
                                    <ToolbarButton
                                        icon={<SupportIcon />}
                                        className="text-green-600"
                                        onClick={() => {
                                            contextualHelp.showContextualHelp(
                                                "/custom-content/entry.html"
                                            );
                                        }}
                                    >
                                        Help
                                    </ToolbarButton>
                                </ToolbarGroup>
                            </Toolbar>
                            {state.loading ? (
                                <Fragment>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                </Fragment>
                            ) : (
                                <div className="space-y-4 px-2 sm:px-0">
                                    <div
                                        className={packClasses(
                                            "space-y-4",
                                            metaOpen ? "" : "hidden"
                                        )}
                                    >
                                        <SelectBox
                                            label="Channel"
                                            name="channel"
                                            id="channel"
                                            observeChange={onChannelChange}
                                            disabled={state.loading}
                                            options={state.channels.map(
                                                (ch) => {
                                                    return {
                                                        value: ch.id,
                                                        label: ch.title,
                                                    };
                                                }
                                            )}
                                        />

                                        <TextInput
                                            label="Title"
                                            name="title"
                                            disabled={
                                                state.saving ||
                                                !state.channel.id
                                            }
                                        />
                                        <TextInput
                                            label="Slug"
                                            name="slug"
                                            disabled={
                                                state.saving ||
                                                !state.channel.id
                                            }
                                            onChange={(e) => {
                                                const newVal = e.target.value
                                                    .toLowerCase()
                                                    .replace(/ {2,}/g, " ")
                                                    .replace(/ /g, "-")
                                                    .replace(/[^a-z0-9-]/g, "");

                                                setFieldValue("slug", newVal);
                                            }}
                                        />
                                    </div>
                                    <div
                                        className={packClasses(
                                            "flex",
                                            metaOpen
                                                ? "justify-end"
                                                : "justify-between"
                                        )}
                                    >
                                        <div
                                            className={packClasses(
                                                "text-sm text-gray-600",
                                                metaOpen ? "hidden" : ""
                                            )}
                                        >
                                            <span className="font-semibold">
                                                {state.channel.title}
                                            </span>{" "}
                                            &gt; {state.entry.title} (
                                            {state.entry.slug})
                                        </div>
                                        <button
                                            type="button"
                                            className="rounded border border-green-500 bg-white px-2 py-1 text-xs uppercase text-black"
                                            onClick={() => {
                                                setMetaOpen(!metaOpen);
                                            }}
                                        >
                                            {metaOpen ? "Hide" : "Show"} Channel
                                            Info
                                        </button>
                                    </div>
                                    {state.channel?.description && (
                                        <div
                                            className="text-sm text-gray-600"
                                            dangerouslySetInnerHTML={{
                                                __html: state.channel
                                                    .description,
                                            }}
                                        ></div>
                                    )}
                                    <hr className="my-5" />
                                    <EntryFields channel={state.channel} />
                                    {!state.channel.id && (
                                        <div className="text-center text-sm tracking-wide text-gray-600">
                                            Please select a channel before
                                            continuing.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </MainBlock>
    );
}
